// Componente per sistema ExeDialog DDT2026 - Pagina unificata pesi
// Riunisce peso specifico (opzionale), tara, lordo e netto calcolato.
// Tutti i widget sono sempre visibili. La pagina si attiva quando
// campo_da_verificare è "tara" o "lordo".
//
// In modalità coupon (SPECCOUPON2026.md) la pagina non mostra il lordo:
// è un dato dinamico, letto dalla bilancia solo all'attivazione del coupon.
// Al suo posto si raccolgono le correzioni di peso dtara/dlordo e si offre
// esplicitamente la tara convenzionale 9999 ("usa la tara del veicolo").
import React, { useState, useRef, useEffect } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { usePesiPonte } from '../queries';
import { isCouponMode, TARA_DA_VEICOLO } from '../couponMode';
import { ValoriVerificati } from './ValoriVerificati';

export function Pesi(props) {
    const { currURLSearchParams, onFinish, verificaData } = props;
    const lordoRef = useRef();
    const taraRef = useRef();

    const isCoupon = isCouponMode(currURLSearchParams);

    const valoriAccessori = verificaData?.valori_accessori || {};
    const valoriVerificati = verificaData?.valori_verificati || {};
    const taraVeicolo = valoriAccessori.tara_veicolo;
    const unita = valoriVerificati.unita || valoriAccessori.unita || 'q.li';
    // Nel coupon il lordo non si acquisisce: niente polling della pesa
    const codBollaPesa = isCoupon
        ? null
        : (valoriAccessori.cod_bolla_pesa || currURLSearchParams.get('cod_bolla'));

    // campo_da_verificare corrente: tara o lordo
    const campoCorrente = verificaData?.campo_da_verificare;

    const [pesospec, setPesospec] = useState(
        currURLSearchParams.get('pesospec') 
            ? parseFloat(currURLSearchParams.get('pesospec')) 
            : null
    );
    const [tara, setTara] = useState(
        currURLSearchParams.get('tara') 
            ? parseFloat(currURLSearchParams.get('tara')) 
            : (taraVeicolo != null ? taraVeicolo : null)
    );
    const [lordo, setLordo] = useState(
        currURLSearchParams.get('lordo') ? parseFloat(currURLSearchParams.get('lordo')) : null
    );
    // Correzioni di peso del coupon, applicate all'emissione del DDT (con segno)
    const [dtara, setDtara] = useState(
        currURLSearchParams.get('dtara') ? parseFloat(currURLSearchParams.get('dtara')) : 0
    );
    const [dlordo, setDlordo] = useState(
        currURLSearchParams.get('dlordo') ? parseFloat(currURLSearchParams.get('dlordo')) : 0
    );
    const [setPesoLordo, setSetPesoLordo] = useState(true);

    // Hook per polling pesi dalla pesa
    const { pesi } = usePesiPonte(codBollaPesa);

    // Tara convenzionale: all'emissione verrà usata veicoli.tara della targa
    const taraDaVeicolo = tara === TARA_DA_VEICOLO;

    // Focus sul campo da compilare
    useEffect(() => {
        if (campoCorrente === 'tara' && taraRef.current) {
            taraRef.current.getInput().focus();
        } else if (campoCorrente === 'lordo' && lordoRef.current) {
            lordoRef.current.getInput().focus();
        }
    }, [campoCorrente, verificaData]);

    // Aggiorna tara suggerita quando arriva valoriAccessori
    useEffect(() => {
        if (taraVeicolo != null && tara === null) {
            setTara(taraVeicolo);
        }
    }, [taraVeicolo, tara]);

    // Aggiorna automaticamente il peso lordo se setPesoLordo è true
    useEffect(() => {
        if (setPesoLordo && pesi.length > 0) {
            const pesoRecente = pesi[0];
            if (pesoRecente.peso > 0) {
                setLordo(pesoRecente.peso / 10);
            }
        }
    }, [pesi, setPesoLordo]);

    // Submit: invia tara, lordo e pesospec insieme.
    // Il server valida e restituisce il primo campo non valido.
    // Nel coupon il lordo non viene inviato (ed eventuali valori residui
    // vengono rimossi dall'URL passando null).
    const handleSubmit = (e) => {
        e.preventDefault();
        if (submitDisabled) return;
        const values = { tara };
        if (pesospec !== null) values.pesospec = pesospec;
        if (isCoupon) {
            values.lordo = null;
            values.dtara = dtara ?? 0;
            values.dlordo = dlordo ?? 0;
        } else {
            values.lordo = lordo;
        }
        onFinish(values);
    };

    const handleManualLordoChange = (value) => {
        setSetPesoLordo(false);
        setLordo(value);
    };

    const handleRowClick = (e) => {
        setSetPesoLordo(false);
        setLordo(e.data.peso / 10);
    };

    const handleGetCurrentWeight = () => {
        if (pesi.length > 0 && pesi[0].peso > 0) {
            setSetPesoLordo(false);
            setLordo(pesi[0].peso / 10);
        }
    };

    // Attiva/disattiva la tara convenzionale 9999
    const handleTaraDaVeicolo = (checked) => {
        setTara(checked ? TARA_DA_VEICOLO : (taraVeicolo != null ? taraVeicolo : null));
    };

    const netto = (lordo || 0) - (tara || 0);
    const taraValid = tara !== null && tara >= 0;
    const lordoValid = lordo !== null && lordo >= 0 && lordo > (tara || 0);

    // Pesi dalla pesa convertiti per la tabella
    const pesiTabella = pesi.map(p => ({
        ...p,
        pesoTonnellate: (p.peso / 10).toFixed(2),
        ora: new Date(p.data).toLocaleTimeString()
    }));

    // Validazione del pulsante: nel coupon basta la tara, nel DDT servono tara e lordo
    const submitDisabled = isCoupon ? !taraValid : (!taraValid || !lordoValid);

    return (
        <div className="card-body">
            <h5 className="card-title">{isCoupon ? 'Pesi del coupon' : 'Pesi'}</h5>
            {isCoupon && (
                <p className="text-muted">
                    Il lordo non si registra nel coupon: viene letto dalla bilancia
                    all&rsquo;attivazione. Le correzioni dtara e dlordo, con segno,
                    vengono applicate all&rsquo;emissione del DDT.
                </p>
            )}

            <form onSubmit={handleSubmit}>
                {/* Peso Specifico - sempre visibile e editabile (opzionale) */}
                <div className="mb-3">
                    <label htmlFor="pesospec" className="form-label">Peso Specifico (opzionale)</label>
                    <InputNumber 
                        id="pesospec"
                        value={pesospec}
                        onValueChange={(e) => setPesospec(e.value)}
                        mode="decimal"
                        locale="en-US"
                        minFractionDigits={3}
                        maxFractionDigits={3}
                        min={0}
                        className="w-100"
                    />
                </div>

                {/* Tara - sempre visibile */}
                <div className="mb-3">
                    <label htmlFor="tara" className="form-label">Tara ({unita})</label>
                    <InputNumber 
                        ref={taraRef}
                        id="tara"
                        value={tara}
                        onValueChange={(e) => setTara(e.value)}
                        mode="decimal"
                        locale="en-US"
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        min={0}
                        disabled={taraDaVeicolo}
                        className="w-100"
                    />
                    {isCoupon && (
                        <div className="form-check mt-2">
                            <Checkbox
                                inputId="tara_da_veicolo"
                                checked={taraDaVeicolo}
                                onChange={(e) => handleTaraDaVeicolo(e.checked)}
                            />
                            <label htmlFor="tara_da_veicolo" className="ms-2">
                                Userai la tara registrata per il veicolo nell'elenco Veicoli
                            </label>
                        </div>
                    )}
                    {!taraDaVeicolo && taraVeicolo != null && (
                        <small className="text-muted d-block">Tara suggerita dal veicolo: {taraVeicolo}</small>
                    )}
                </div>

                {isCoupon ? (
                    /* Correzioni di peso del coupon: ammesse positive e negative */
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label htmlFor="dtara" className="form-label">Correzione tara &mdash; dtara ({unita})</label>
                            <InputNumber
                                id="dtara"
                                value={dtara}
                                onValueChange={(e) => setDtara(e.value ?? 0)}
                                mode="decimal"
                                locale="en-US"
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                className="w-100"
                            />
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="dlordo" className="form-label">Correzione lordo &mdash; dlordo ({unita})</label>
                            <InputNumber
                                id="dlordo"
                                value={dlordo}
                                onValueChange={(e) => setDlordo(e.value ?? 0)}
                                mode="decimal"
                                locale="en-US"
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                className="w-100"
                            />
                        </div>
                    </div>
                ) : (
                <>
                {/* Lordo - sempre visibile */}
                <div className="mb-3">
                    <label htmlFor="lordo" className="form-label">Lordo ({unita})</label>
                    <div className="d-flex gap-2 align-items-center">
                        <InputNumber 
                            ref={lordoRef}
                            id="lordo"
                            value={lordo}
                            onValueChange={(e) => handleManualLordoChange(e.value)}
                            mode="decimal"
                            locale="en-US"
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            min={0}
                            className="flex-grow-1"
                        />
                        <Button 
                            icon="pi pi-arrow-left" 
                            type="button"
                            onClick={handleGetCurrentWeight}
                            disabled={pesi.length === 0 || pesi[0].peso === 0}
                            title="Usa peso corrente"
                            severity="secondary"
                        />
                    </div>
                    {setPesoLordo && pesi.length > 0 && (
                        <small className="text-success">Aggiornamento automatico attivo</small>
                    )}
                </div>

                {/* Netto calcolato */}
                <div className="row mb-3">
                    <div className="col-md-4">
                        <label className="form-label">Netto ({unita})</label>
                        <InputNumber value={netto > 0 ? netto : 0} disabled mode="decimal" locale="en-US" minFractionDigits={2} maxFractionDigits={2} className="w-100" />
                    </div>
                </div>

                {!lordoValid && lordo !== null && lordo > 0 && (
                    <div className="alert alert-warning">
                        Il lordo deve essere maggiore della tara ({tara})
                    </div>
                )}
                </>
                )}

                <button className="btn btn-primary mb-3" disabled={submitDisabled}>
                    Prosegui
                </button>

                {!isCoupon && pesiTabella.length > 0 && (
                    <div className="card mb-3">
                        <DataTable 
                            value={pesiTabella} 
                            size="small"
                            scrollable
                            scrollHeight="400px"
                            onRowClick={handleRowClick}
                            selectionMode="single"
                            rowClassName={() => 'cursor-pointer'}
                            header="Pesi rilevati dalla pesa"
                        >
                            <Column field="pesoTonnellate" header={`Peso (${unita})`} style={{ width: '50%' }} sortable />
                            <Column field="ora" header="Ora" style={{ width: '50%' }} sortable />
                        </DataTable>
                    </div>
                )}
            </form>
            
            <ValoriVerificati valori={verificaData?.valori_verificati} />
        </div>
    );
}

// La pagina si attiva quando campo_da_verificare è "tara" o "lordo"
// (in modalità coupon il server non chiede mai "lordo")
Pesi.displayName = 'tara';
Pesi.handledFields = ['tara', 'lordo'];
